"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhatGetsLabeledInATreatmentPlant() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const experiences = [{
    number: "01",
    title: "Industrial Asset Identification",
    description: "Precision-engineered labeling solutions designed to withstand the harsh, corrosive environments of modern water treatment facilities.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/treatment_plant_labels.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/treatment_plant_labels.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/treatment_plant_labels.png-wvc-sizes",
    alt: "Industrial asset labels in a treatment plant",
    link: "/ProductsPage"
  }, {
    number: "02",
    title: "Safety & Compliance Signage",
    description: "High-visibility, durable safety markers that ensure full regulatory compliance and operational clarity for your facility staff.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/safety_compliance_signage.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/safety_compliance_signage.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/safety_compliance_signage.png-wvc-sizes",
    alt: "Safety and compliance signage for industrial plants",
    link: "/ArcFlashLabelsPage"
  }];
  return <section data-section-id="4486" id="what-gets-labeled-in-a-treatment-plant" className="relative bg-background text-foreground py-32 md:py-44 lg:py-[180px] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {}
        <div ref={headerRef} className={`text-center mb-20 md:mb-28 transition-all duration-[900ms] ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs md:text-sm uppercase tracking-[0.3em] mb-8 text-chart-3">
            Our Core Solutions
          </p>
          <h2 className="font-default font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[72px] leading-[1.05] tracking-tight max-w-[18ch] mx-auto">
            Essential labeling for critical infrastructure.
          </h2>
        </div>

        {}
        <div ref={gridRef} className={`relative grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-0 transition-all duration-[900ms] ease-out delay-150 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="hidden md:flex absolute left-1/2 top-0 bottom-0 -translate-x-1/2 items-center justify-center pointer-events-none" aria-hidden="true">
            <div className="relative h-full w-px bg-chart-3 opacity-40">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-lg bg-chart-3" />
            </div>
          </div>

          {}

          {experiences.map((exp, i) => <div key={exp.number} data-index={i} className={`group relative ${i === 0 ? "md:pr-16 lg:pr-20" : "md:pl-16 lg:pl-20"}`}>
              {}
              <div className="relative overflow-hidden mb-8 aspect-[3/4] rounded-lg shadow-lg">
                <img src={exp.image} data-wvc-srcset={exp.srcset} data-wvc-sizes={exp.sizes} alt={exp.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.04]" />
              </div>

              {}
              <div className="font-default text-sm tracking-[0.2em] mb-4 text-chart-3">
                {exp.number}
              </div>

              {}
              <h3 className="font-default font-bold text-2xl md:text-3xl lg:text-[32px] leading-tight mb-4 transition-transform duration-500 ease-out group-hover:translate-x-1.5">
                {exp.title}
              </h3>

              {}
              <p className="font-default text-base leading-relaxed mb-6 max-w-md text-muted-foreground">
                {exp.description}
              </p>

              {}
              <Link to={exp.link} className="inline-flex items-center gap-2 font-default text-sm tracking-wider uppercase pb-1 border-b transition-all duration-500 text-chart-3 border-chart-3">
                <span className="relative">
                  View product details
                </span>
                <ArrowUpRight className="size-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>

              {}
              {i === 0 && <div className="md:hidden mt-16 flex items-center justify-center" aria-hidden="true">
                  <div className="relative w-full h-px bg-chart-3 opacity-40">
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-lg bg-chart-3" />
                  </div>
                </div>}
            </div>)}
        </div>

        {}
        <div ref={ctaRef} className={`mt-20 md:mt-28 text-center transition-all duration-[900ms] ease-out delay-300 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Link to="/ProductsPage" className="inline-flex items-center gap-2 font-default text-xs md:text-sm uppercase tracking-[0.25em] pb-1 border-b transition-opacity duration-300 hover:opacity-80 text-chart-3 border-chart-3">
            Explore all solutions
            <ArrowUpRight className="size-3.5" />
          </Link>
        </div>
      </div>
    </section>;
}